import React, { useEffect, useMemo, useState } from "react";
import api from "../lib/api";
import { PageHeader } from "../components/TaskTable";
import TaskDrawer from "../components/TaskDrawer";
import { GitFork, Table as TableIcon, Network } from "lucide-react";

const PRIO = { Critical: "#DC2626", High: "#EA580C", Medium: "#2563EB", Low: "#94A3B8" };
const NODE_W = 190, NODE_H = 56, COL_W = 250, ROW_H = 82, PAD = 24;

function layout(deps, tasks) {
  const nodes = new Set();
  deps.forEach((d) => { if (tasks[d.predecessor_id]) nodes.add(d.predecessor_id); if (tasks[d.successor_id]) nodes.add(d.successor_id); });
  const succ = {}, indeg = {};
  nodes.forEach((n) => { succ[n] = []; indeg[n] = 0; });
  const edges = [];
  deps.forEach((d) => {
    if (!nodes.has(d.predecessor_id) || !nodes.has(d.successor_id)) return;
    succ[d.predecessor_id].push(d.successor_id);
    indeg[d.successor_id]++;
    edges.push({ ...d });
  });
  // longest-path levels (Kahn)
  const level = {}; nodes.forEach((n) => (level[n] = 0));
  const q = [...nodes].filter((n) => indeg[n] === 0);
  const deg = { ...indeg };
  while (q.length) {
    const n = q.shift();
    succ[n].forEach((s) => { level[s] = Math.max(level[s], level[n] + 1); if (--deg[s] === 0) q.push(s); });
  }
  const cols = {};
  [...nodes].sort((a, b) => (tasks[a]?.human_id || "").localeCompare(tasks[b]?.human_id || "")).forEach((n) => {
    (cols[level[n]] = cols[level[n]] || []).push(n);
  });
  const pos = {};
  Object.entries(cols).forEach(([lv, arr]) => arr.forEach((n, i) => { pos[n] = { x: PAD + Number(lv) * COL_W, y: PAD + i * ROW_H }; }));
  const maxLevel = Math.max(0, ...Object.keys(cols).map(Number));
  const maxRows = Math.max(1, ...Object.values(cols).map((a) => a.length));
  return { nodes: [...nodes], edges, pos, width: PAD * 2 + maxLevel * COL_W + NODE_W, height: PAD * 2 + maxRows * ROW_H };
}

function Graph({ deps, tasks, onOpen }) {
  const g = useMemo(() => layout(deps, tasks), [deps, tasks]);
  if (g.nodes.length === 0) return <div className="py-16 text-center text-slate-400 text-sm">No linked tasks to graph yet.</div>;
  return (
    <div className="overflow-auto bg-white border rounded-xl p-2" style={{ maxHeight: "70vh" }}>
      <svg width={g.width} height={g.height} data-testid="dependency-graph">
        <defs>
          <marker id="arrow" markerWidth="8" markerHeight="8" refX="7" refY="3" orient="auto" markerUnits="strokeWidth">
            <path d="M0,0 L7,3 L0,6 Z" fill="#94a3b8" />
          </marker>
        </defs>
        {g.edges.map((e, i) => {
          const a = g.pos[e.predecessor_id], b = g.pos[e.successor_id];
          if (!a || !b) return null;
          const x1 = a.x + NODE_W, y1 = a.y + NODE_H / 2, x2 = b.x, y2 = b.y + NODE_H / 2;
          const mx = (x1 + x2) / 2;
          return <path key={i} d={`M${x1},${y1} C${mx},${y1} ${mx},${y2} ${x2},${y2}`}
            fill="none" stroke={e.hard ? "#f87171" : "#cbd5e1"} strokeWidth={e.hard ? 2 : 1.5}
            strokeDasharray={e.hard ? "0" : "4 3"} markerEnd="url(#arrow)" />;
        })}
        {g.nodes.map((n) => {
          const t = tasks[n]; const p = g.pos[n];
          const closed = ["Completed", "Closed", "Cancelled"].includes(t?.execution_status);
          const border = PRIO[t?.priority] || "#94A3B8";
          const fill = t?.blocked ? "#fef2f2" : t?.is_overdue ? "#fff7ed" : "#ffffff";
          return (
            <g key={n} transform={`translate(${p.x},${p.y})`} style={{ cursor: "pointer" }} onClick={() => onOpen(n)} data-testid={`graph-node-${t?.human_id}`}>
              <rect width={NODE_W} height={NODE_H} rx="8" fill={fill} stroke={border} strokeWidth="2" opacity={closed ? 0.6 : 1} />
              <text x="10" y="20" fontSize="10" fill="#94a3b8" fontFamily="monospace">{t?.human_id}</text>
              <text x="10" y="38" fontSize="12" fill="#1e293b" fontWeight="600">{(t?.title || "").slice(0, 24)}{(t?.title || "").length > 24 ? "…" : ""}</text>
              {t?.blocked && <circle cx={NODE_W - 12} cy="14" r="5" fill="#dc2626" />}
            </g>
          );
        })}
      </svg>
    </div>
  );
}

export default function Dependencies() {
  const [deps, setDeps] = useState([]);
  const [tasks, setTasks] = useState({});
  const [openId, setOpenId] = useState(null);
  const [view, setView] = useState("graph");

  const load = async () => {
    const [d, t] = await Promise.all([api.get("/dependencies"), api.get("/tasks", { params: { page_size: 500 } })]);
    setDeps(d.data);
    const map = {}; t.data.items.forEach((x) => (map[x.id] = x));
    setTasks(map);
  };
  useEffect(() => { load(); }, []);

  return (
    <div className="p-6">
      <PageHeader title="Dependencies" subtitle={`${deps.length} cross-task links · circular dependencies are automatically prevented`}>
        <div className="flex rounded-md border overflow-hidden">
          <button onClick={() => setView("graph")} data-testid="dep-view-graph" className={`px-3 py-1.5 text-xs font-medium flex items-center gap-1.5 ${view === "graph" ? "bg-slate-900 text-white" : "bg-white text-slate-600"}`}><Network size={14} />Graph</button>
          <button onClick={() => setView("table")} data-testid="dep-view-table" className={`px-3 py-1.5 text-xs font-medium flex items-center gap-1.5 ${view === "table" ? "bg-slate-900 text-white" : "bg-white text-slate-600"}`}><TableIcon size={14} />Table</button>
        </div>
      </PageHeader>

      {view === "graph" && (
        <>
          <div className="flex flex-wrap items-center gap-4 mb-3 text-xs text-slate-500">
            <span className="flex items-center gap-1.5"><span className="inline-block w-5 border-t-2 border-red-400" />Hard link</span>
            <span className="flex items-center gap-1.5"><span className="inline-block w-5 border-t-2 border-dashed border-slate-300" />Soft link</span>
            <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full bg-red-600" />Blocked</span>
            <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-sm bg-orange-100 border border-orange-400" />Overdue</span>
          </div>
          <Graph deps={deps} tasks={tasks} onOpen={setOpenId} />
        </>
      )}

      {view === "table" && (
        <div className="bg-white border rounded-xl overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-left border-b bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
              <th className="px-4 py-2.5">Predecessor</th><th className="px-4 py-2.5"></th><th className="px-4 py-2.5">Successor</th>
              <th className="px-4 py-2.5">Type</th><th className="px-4 py-2.5">Strength</th><th className="px-4 py-2.5">Impact</th>
            </tr></thead>
            <tbody>
              {deps.map((d) => {
                const p = tasks[d.predecessor_id], s = tasks[d.successor_id];
                return (
                  <tr key={d.id} className="border-b last:border-0 hover:bg-slate-50">
                    <td className="px-4 py-2.5">
                      <button onClick={() => p && setOpenId(p.id)} className="text-left hover:text-blue-600">
                        <span className="font-mono text-xs text-slate-400">{p?.human_id}</span><div className="font-medium">{p?.title || "—"}</div>
                      </button>
                    </td>
                    <td className="px-2 text-slate-300"><GitFork size={16} /></td>
                    <td className="px-4 py-2.5">
                      <button onClick={() => s && setOpenId(s.id)} className="text-left hover:text-blue-600">
                        <span className="font-mono text-xs text-slate-400">{s?.human_id}</span><div className="font-medium">{s?.title || "—"}</div>
                      </button>
                    </td>
                    <td className="px-4 py-2.5 text-slate-600 whitespace-nowrap">{d.dep_type}</td>
                    <td className="px-4 py-2.5"><span className={`text-xs px-2 py-0.5 rounded ${d.hard ? "bg-red-50 text-red-600" : "bg-slate-100 text-slate-500"}`}>{d.hard ? "Hard" : "Soft"}</span></td>
                    <td className="px-4 py-2.5 text-slate-500 text-xs">{d.impact || "—"}</td>
                  </tr>
                );
              })}
              {deps.length === 0 && <tr><td colSpan={6} className="py-12 text-center text-slate-400">No dependencies yet. Add them from a task's Dependency action.</td></tr>}
            </tbody>
          </table>
        </div>
      )}
      {openId && <TaskDrawer taskId={openId} onClose={() => setOpenId(null)} onChanged={load} />}
    </div>
  );
}
